import { Combobox } from '@base-ui/react/combobox';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import * as React from 'react';

import type { ListedUser } from '~/lib/api';
import { cn } from '~/lib/utils';

export type EntitySelectOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

type SearchableEntitySelectProps = {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: EntitySelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  popupClassName?: string;
};

type UserSelectProps = Omit<
  SearchableEntitySelectProps,
  'options' | 'searchPlaceholder' | 'emptyMessage'
> & {
  users: ListedUser[];
  includeAll?: boolean;
  allLabel?: string;
};

type RoomSelectProps<Room extends { id: string; name: string }> = Omit<
  SearchableEntitySelectProps,
  'options' | 'searchPlaceholder' | 'emptyMessage'
> & {
  rooms: Room[];
  includeAll?: boolean;
  allLabel?: string;
};

type GroupSelectProps<Group extends { id: string; name: string }> = Omit<
  SearchableEntitySelectProps,
  'options' | 'searchPlaceholder' | 'emptyMessage'
> & {
  groups: Group[];
  includeAll?: boolean;
  allLabel?: string;
};

function normalizeSearch(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function optionMatchesSearch(
  option: EntitySelectOption,
  query: string
): boolean {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) return true;

  const haystack = [option.label, option.description, option.value]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase();
  return haystack.includes(normalizedQuery);
}

export function SearchableEntitySelect({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  ariaLabel,
  disabled,
  className,
  popupClassName
}: SearchableEntitySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const selectedOption =
    options.find((option) => option.value === value) ?? null;
  const hasSearchQuery = normalizeSearch(inputValue).length > 0;
  const hasMatchingOptions = options.some((option) =>
    optionMatchesSearch(option, inputValue)
  );

  return (
    <Combobox.Root
      value={selectedOption}
      onValueChange={(nextValue) => {
        onValueChange(Array.isArray(nextValue) ? '' : (nextValue?.value ?? ''));
        setInputValue('');
        setOpen(false);
      }}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      items={options}
      itemToStringLabel={(option) => option.label}
      itemToStringValue={(option) => option.value}
      isItemEqualToValue={(item, selected) => item.value === selected.value}
      filter={optionMatchesSearch}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setInputValue('');
      }}
      autoHighlight
      disabled={disabled}
    >
      <Combobox.Trigger
        id={id}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm shadow-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground',
          className
        )}
        aria-label={ariaLabel ?? placeholder}
      >
        <span
          title={selectedOption?.label}
          className={cn(
            'min-w-0 flex-1 truncate',
            !selectedOption && 'text-muted-foreground'
          )}
        >
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
      </Combobox.Trigger>
      <Combobox.Portal>
        <Combobox.Positioner
          side="bottom"
          align="start"
          sideOffset={4}
          collisionAvoidance={{
            side: 'flip',
            align: 'shift',
            fallbackAxisSide: 'none'
          }}
          className="z-[80] max-w-[var(--available-width)]"
        >
          <Combobox.Popup
            className={cn(
              'max-h-[min(var(--available-height),20rem)] w-max max-w-[min(28rem,var(--available-width))] min-w-[max(var(--anchor-width),14rem)] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md outline-none origin-[var(--transform-origin)] data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 data-[closed]:zoom-out-95 data-[open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
              popupClassName
            )}
          >
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <Combobox.Input
                className="h-8 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
                placeholder={searchPlaceholder}
              />
            </div>
            {hasSearchQuery && !hasMatchingOptions ? (
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="px-3 py-6 text-center text-sm text-muted-foreground"
              >
                {emptyMessage}
              </div>
            ) : null}
            <Combobox.List className="max-h-[calc(min(var(--available-height),20rem)_-_3.25rem)] overflow-y-auto p-1">
              <Combobox.Collection>
                {(option: EntitySelectOption, index) => (
                  <Combobox.Item
                    key={option.value}
                    value={option}
                    index={index}
                    disabled={option.disabled}
                    className="relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 pr-8 text-sm outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block break-all">{option.label}</span>
                      {option.description ? (
                        <span className="block text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    <Combobox.ItemIndicator className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Check className="h-4 w-4" aria-hidden />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                )}
              </Combobox.Collection>
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}

export function UserSelect({
  users,
  includeAll,
  allLabel = 'All users',
  className,
  popupClassName,
  ...props
}: UserSelectProps) {
  const options = React.useMemo<EntitySelectOption[]>(() => {
    const userOptions = [...users]
      .sort((a, b) => a.email.localeCompare(b.email))
      .map((user) => ({
        value: user.id,
        label: user.email,
        description: user.deactivatedAt ? 'Deactivated' : user.role
      }));
    return includeAll
      ? [{ value: 'all', label: allLabel }, ...userOptions]
      : userOptions;
  }, [allLabel, includeAll, users]);

  return (
    <SearchableEntitySelect
      {...props}
      className={cn('min-w-[17.5rem]', className)}
      popupClassName={cn(
        'min-w-[max(var(--anchor-width),20rem)]',
        popupClassName
      )}
      options={options}
      searchPlaceholder="Search users..."
      emptyMessage="No users found."
    />
  );
}

export function RoomSelect<Room extends { id: string; name: string }>({
  rooms,
  includeAll,
  allLabel = 'All rooms',
  className,
  popupClassName,
  ...props
}: RoomSelectProps<Room>) {
  const options = React.useMemo<EntitySelectOption[]>(() => {
    const roomOptions = [...rooms]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((room) => ({
        value: room.id,
        label: room.name
      }));
    return includeAll
      ? [{ value: 'all', label: allLabel }, ...roomOptions]
      : roomOptions;
  }, [allLabel, includeAll, rooms]);

  return (
    <SearchableEntitySelect
      {...props}
      className={cn('min-w-[14rem]', className)}
      popupClassName={cn(
        'min-w-[max(var(--anchor-width),16rem)]',
        popupClassName
      )}
      options={options}
      searchPlaceholder="Search rooms..."
      emptyMessage="No rooms found."
    />
  );
}

export function GroupSelect<Group extends { id: string; name: string }>({
  groups,
  includeAll,
  allLabel = 'All groups',
  ...props
}: GroupSelectProps<Group>) {
  const options = React.useMemo<EntitySelectOption[]>(() => {
    const groupOptions = [...groups]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((group) => ({
        value: group.id,
        label: group.name
      }));
    return includeAll
      ? [{ value: 'all', label: allLabel }, ...groupOptions]
      : groupOptions;
  }, [allLabel, includeAll, groups]);

  return (
    <SearchableEntitySelect
      {...props}
      options={options}
      searchPlaceholder="Search groups..."
      emptyMessage="No groups found."
    />
  );
}
